import React, { useState } from "react";
import axios from "axios";
import { CalendarCheck, Users, Clock, User, Phone, Mail } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../i18n/LanguageContext";
import SectionBg from "./SectionBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reservation() {
  const { t } = useLang();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", time: "", guests: 2, notes: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.time) {
      toast({ title: t("reservation.errTitle"), description: t("reservation.errDesc") });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/reservations`, { ...form, guests: Number(form.guests) });
      setForm({ name: "", phone: "", email: "", date: "", time: "", guests: 2, notes: "" });
      toast({ title: t("reservation.okTitle"), description: t("reservation.okDesc") });
    } catch (err) {
      toast({ title: t("reservation.failTitle"), description: t("reservation.failDesc") });
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-4 py-3 text-[#23201C] outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";
  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="prenota" className="relative py-24 md:py-32 bg-[#f3ecdf] overflow-hidden">
      <SectionBg section="reservation" />
      <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8">
        <div className="text-center mb-12 reveal">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-12 bg-[#C9A24B]" />
            <span className="text-[#B0863B] tracking-[0.3em] text-xs uppercase">{t("reservation.badge")}</span>
            <span className="h-px w-12 bg-[#C9A24B]" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[#23201C] leading-tight mb-4">
            {t("reservation.title1")} <span className="italic text-[#C9A24B]">{t("reservation.highlight")}</span>
          </h2>
          <p className="text-[#7c7264] max-w-xl mx-auto">{t("reservation.subtitle")}</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-[2px] p-8 md:p-10 shadow-xl border border-[#efe6d4] reveal">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="flex items-center gap-2 text-sm text-[#5a5347] mb-2 font-medium"><User size={15} className="text-[#C9A24B]" />{t("reservation.name")}</label>
              <input name="name" value={form.name} onChange={onChange} className={inputCls} />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-[#5a5347] mb-2 font-medium"><Phone size={15} className="text-[#C9A24B]" />{t("reservation.phone")}</label>
              <input name="phone" value={form.phone} onChange={onChange} className={inputCls} placeholder="+39 ..." />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-[#5a5347] mb-2 font-medium"><Mail size={15} className="text-[#C9A24B]" />{t("reservation.email")}</label>
              <input type="email" name="email" value={form.email} onChange={onChange} className={inputCls} placeholder={t("reservation.emailPh")} />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-[#5a5347] mb-2 font-medium"><CalendarCheck size={15} className="text-[#C9A24B]" />{t("reservation.date")}</label>
              <input type="date" name="date" min={today} value={form.date} onChange={onChange} className={inputCls} />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-[#5a5347] mb-2 font-medium"><Clock size={15} className="text-[#C9A24B]" />{t("reservation.time")}</label>
              <input type="time" name="time" value={form.time} onChange={onChange} className={inputCls} />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-[#5a5347] mb-2 font-medium"><Users size={15} className="text-[#C9A24B]" />{t("reservation.guests")}</label>
              <input type="number" name="guests" min={1} max={50} value={form.guests} onChange={onChange} className={inputCls} />
            </div>
            <div className="md:col-span-1">
              <label className="block text-sm text-[#5a5347] mb-2 font-medium">{t("reservation.notes")}</label>
              <input name="notes" value={form.notes} onChange={onChange} className={inputCls} placeholder={t("reservation.notesPh")} />
            </div>
          </div>
          <button type="submit" disabled={sending}
            className="mt-8 w-full flex items-center justify-center gap-2 bg-[#1C1815] hover:bg-[#2a241f] text-[#C9A24B] px-6 py-4 rounded-full font-medium tracking-wide transition-all duration-300 disabled:opacity-60">
            {sending ? t("reservation.sending") : t("reservation.submit")}
            {!sending && <CalendarCheck size={17} />}
          </button>
        </form>
      </div>
    </section>
  );
}